import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Heart, ShieldCheck, Globe, Store, ArrowRight, Sparkles, CheckCircle2 } from 'lucide-react';

export default function AboutPage() {
  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="about-page">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border/60">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-primary/5 via-background to-background" />
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <span
            className="text-xs font-bold uppercase tracking-[0.2em] text-primary bg-primary/10 px-4 py-2 rounded-full inline-block mb-6"
            data-testid="about-eyebrow"
          >
            Our Story
          </span>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium text-foreground mb-6"
            data-testid="about-title"
          >
            A marketplace built on faith, fairness, and family.
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-3xl">
            Christ Kingdom Global Marketplace exists for one reason: to give
            Christian artisans, vendors, and entrepreneurs a place to do
            business without compromising their values — and to give buyers
            a place to shop with confidence that every dollar supports a
            household of faith.
          </p>
        </div>
      </section>

      {/* FOUNDER LETTER */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="prose prose-lg max-w-none">
          <h2 className="text-2xl md:text-3xl font-heading font-medium mb-6" data-testid="about-letter-heading">
            A letter from the founder
          </h2>
          <div className="space-y-5 text-foreground/90 leading-relaxed text-base md:text-lg">
            <p>
              I built Christ Kingdom Global Marketplace because the believers
              I love kept telling me the same story: <em>“I have a real
              product, a real ministry, a real calling — but every platform I
              join treats me like a number, takes too much in fees, and surrounds
              my listings with things that don&rsquo;t honor my faith.”</em>
            </p>
            <p>
              We decided to do something different. We charge a flat <strong>10%</strong>
              platform fee, vendors keep <strong>90%</strong>, and payouts are
              automated through Stripe Connect — no PayPal disputes, no manual
              chasing. Every vendor signs the <Link to="/vendor/covenant" className="text-primary underline">Vendor Covenant</Link>{' '}
              before they can list a product, so buyers know what they&rsquo;re
              walking into.
            </p>
            <p>
              We&rsquo;re Christian-led, global by design, and we built this
              platform so that a single mom in Lagos, a worship pastor in
              Nashville, and an oil trader in Houston can all sell, prosper,
              and bless their families through honest commerce — side by side.
            </p>
            <p className="text-muted-foreground italic">
              — The Christ Kingdom Global Marketplace team
            </p>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="bg-muted/30 border-y border-border/60">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-16">
          <h2 className="text-2xl md:text-3xl font-heading font-medium mb-10 text-center" data-testid="about-pillars-heading">
            What we stand on
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: ShieldCheck,
                title: 'Vendor Covenant',
                body: 'Every seller signs our written covenant of conduct, faith, and fair dealing before going live. Listings that violate it are removed — no exceptions.',
              },
              {
                icon: Heart,
                title: 'Fair Economics',
                body: '10% platform fee. Period. No hidden listing fees, no boost-to-be-seen scheme. Vendors keep 90% of every sale and get paid automatically via Stripe Connect.',
              },
              {
                icon: Globe,
                title: 'Global Reach',
                body: 'Buyers and sellers across 100+ countries. We localize currency, language, and shipping so a small artisan can reach a global congregation.',
              },
            ].map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.title}
                  className="bg-background rounded-2xl border border-border p-6 hover:border-primary/40 hover:shadow-md transition"
                  data-testid={`about-pillar-${p.title.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  <Icon className="h-8 w-8 text-primary mb-4" />
                  <h3 className="text-lg font-medium mb-2">{p.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{p.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHAT YOU&rsquo;LL FIND */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <Sparkles className="h-8 w-8 text-primary mb-4" />
            <h2 className="text-2xl md:text-3xl font-heading font-medium mb-4" data-testid="about-catalog-heading">
              From handmade crosses to crude oil cargoes.
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-6">
              We don&rsquo;t pretend to be one thing. Christ Kingdom Global Marketplace
              is the only platform on earth where a worship-leader can sell a
              custom T-shirt, a Nigerian seamstress can sell modest fashion, and
              a B2B trader can transact a multi-million-dollar Crude Oil or Jet A1
              contract — all under one Christian-led roof.
            </p>
            <ul className="space-y-2 text-sm">
              {[
                'Handmade & faith-based goods',
                'Print-on-demand Christian apparel',
                'Modest fashion from global artisans',
                'B2B Oil & Gas commodity procurement',
                'Vendor storefronts with KYC verification',
              ].map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-background rounded-3xl p-8 border border-primary/20">
            <h3 className="font-medium text-lg mb-4">By the numbers</h3>
            <div className="grid grid-cols-2 gap-6">
              {[
                { k: '10%', v: 'Flat platform fee' },
                { k: '90%', v: 'Kept by the vendor' },
                { k: '100+', v: 'Countries served' },
                { k: '24/7', v: 'Stripe-powered payouts' },
              ].map((s) => (
                <div key={s.v}>
                  <div className="text-3xl font-medium text-primary">{s.k}</div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wide mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-foreground text-background">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-20 text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-medium mb-4" data-testid="about-cta-heading">
            Want to be part of what we&rsquo;re building?
          </h2>
          <p className="text-background/70 max-w-2xl mx-auto mb-8">
            Whether you&rsquo;re a buyer who wants to shop with intention or
            a seller who wants to keep 90% of what you earn — there&rsquo;s a place
            for you here.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/products">
              <Button size="lg" data-testid="about-cta-shop">
                <Store className="h-5 w-5 mr-2" /> Start shopping
              </Button>
            </Link>
            <Link to="/vendor/apply">
              <Button size="lg" variant="outline" className="bg-transparent text-background border-background/30 hover:bg-background/10" data-testid="about-cta-sell">
                Become a vendor <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
